/**
 * Date utility functions for EAC
 * Standard date format: DDMMYY (e.g., "020625" = June 2, 2025)
 */

/**
 * Format a date to DDMMYY format
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Date in DDMMYY format
 */
export const formatToDDMMYY = (date) => {
  if (!date) return "";
  
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    
    return `${day}${month}${year}`;
  } catch {
    return "";
  }
};

/**
 * Parse DDMMYY format to Date object
 * @param {string} dateStr - Date in DDMMYY format (e.g., "020625")
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDDMMYY = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string" || dateStr.length !== 6) {
    return null;
  }
  
  try {
    const day = parseInt(dateStr.substring(0, 2), 10);
    const month = parseInt(dateStr.substring(2, 4), 10) - 1; // JS months are 0-indexed
    const year = 2000 + parseInt(dateStr.substring(4, 6), 10);
    
    const date = new Date(year, month, day);
    
    // Validate the date is real
    if (date.getDate() !== day || date.getMonth() !== month) {
      return null;
    }
    
    return date;
  } catch {
    return null;
  }
};

/**
 * Format DDMMYY to display string
 * @param {string} dateStr - Date in DDMMYY format
 * @param {string} format - 'raw' (020625), 'short' (02/06/25), 'medium' (02 Jun 25), 'long' (02 June 2025)
 * @returns {string} Formatted date string
 */
export const formatDDMMYYDisplay = (dateStr, format = "raw") => {
  if (!dateStr) return "-";
  
  // If already in DDMMYY format
  if (typeof dateStr === "string" && dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    // Raw format - return as-is (same as Shipment Costing)
    if (format === "raw") {
      return dateStr;
    }
    
    const day = dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const year = dateStr.substring(4, 6);
    
    if (format === "short") {
      return `${day}/${month}/${year}`;
    }
    
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthsLong = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const monthIdx = parseInt(month, 10) - 1;
    
    if (format === "medium") {
      return `${day} ${months[monthIdx]} ${year}`;
    }
    
    if (format === "long") {
      return `${day} ${monthsLong[monthIdx]} 20${year}`;
    }
    
    return dateStr; // Default to raw
  }
  
  // If it's an ISO date or other format, parse and convert
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const ddmmyy = formatToDDMMYY(date);
      return formatDDMMYYDisplay(ddmmyy, format);
    }
  } catch {
    // Fall through
  }
  
  // Return as-is if we can't parse it
  return dateStr;
};

/**
 * Get today's date in DDMMYY format
 * @returns {string} Today's date in DDMMYY format
 */
export const getTodayDDMMYY = () => {
  return formatToDDMMYY(new Date());
};

/**
 * Compare two DDMMYY dates
 * @param {string} date1 - First date in DDMMYY format
 * @param {string} date2 - Second date in DDMMYY format
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDDMMYY = (date1, date2) => {
  const d1 = parseDDMMYY(date1);
  const d2 = parseDDMMYY(date2);
  
  if (!d1 && !d2) return 0;
  if (!d1) return -1;
  if (!d2) return 1;
  
  return d1.getTime() - d2.getTime();
};

/**
 * Check if a DDMMYY date is valid
 * @param {string} dateStr - Date in DDMMYY format
 * @returns {boolean} True if valid
 */
export const isValidDDMMYY = (dateStr) => {
  return parseDDMMYY(dateStr) !== null;
};

/**
 * Convert any date format to DDMMYY
 * Handles: DDMMYY, YYYY-MM-DD, ISO strings, Date objects
 * @param {string|Date} date - Input date
 * @returns {string} Date in DDMMYY format
 */
export const toDDMMYY = (date) => {
  if (!date) return "";
  
  // Already in DDMMYY format
  if (typeof date === "string" && date.length === 6 && /^\d{6}$/.test(date)) {
    return date;
  }
  
  // ISO date (YYYY-MM-DD)
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    const d = new Date(date);
    return formatToDDMMYY(d);
  }
  
  // Date object
  if (date instanceof Date) {
    return formatToDDMMYY(date);
  }
  
  // Try parsing as Date
  try {
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      return formatToDDMMYY(d);
    }
  } catch {
    // Fall through
  }
  
  return "";
};

/**
 * Format any date to dd-mm-yy display format
 * Handles: ISO dates (YYYY-MM-DD), Excel serial numbers, Date objects, DDMMYY strings
 * @param {string|Date|number} date - Input date
 * @returns {string} Date in dd-mm-yy format (e.g., "23-01-26")
 */
export const formatDateDashDisplay = (date) => {
  if (!date) return "-";
  
  try {
    let d;
    
    // Already in dd-mm-yy format (with dashes)
    if (typeof date === "string" && /^\d{2}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // DDMMYY format (6 digits)
    if (typeof date === "string" && date.length === 6 && /^\d{6}$/.test(date)) {
      const day = date.substring(0, 2);
      const month = date.substring(2, 4);
      const year = date.substring(4, 6);
      return `${day}-${month}-${year}`;
    }
    
    // Excel serial number (numeric string or number between 1 and 100000)
    const numValue = parseFloat(date);
    if (!isNaN(numValue) && numValue > 1 && numValue < 100000) {
      // Excel serial date: days since 1899-12-30
      const excelEpoch = new Date(1899, 11, 30);
      d = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
    }
    // ISO date string (YYYY-MM-DD...)
    else if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      d = new Date(date);
    }
    // Date object
    else if (date instanceof Date) {
      d = date;
    }
    // Try parsing as Date
    else {
      d = new Date(date);
    }
    
    if (d && !isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    }
  } catch {
    // Fall through
  }
  
  // Return as-is if we can't parse it
  return String(date).substring(0, 10);
};


/**
 * Parse any date format to get year, month, day values
 * Handles: DDMMYY, DD-MM-YY, YYYY-MM-DD, ISO strings, Date objects
 * @param {string|Date} date - Input date
 * @returns {object|null} { year, month, day } or null if invalid
 */
export const parseDateComponents = (date) => {
  if (!date) return null;
  
  try {
    // DDMMYY format (6 digits, e.g., "280226")
    if (typeof date === "string" && date.length === 6 && /^\d{6}$/.test(date)) {
      return {
        day: parseInt(date.substring(0, 2), 10),
        month: parseInt(date.substring(2, 4), 10),
        year: 2000 + parseInt(date.substring(4, 6), 10)
      };
    }
    
    // DD-MM-YY format (e.g., "28-02-26")
    if (typeof date === "string" && /^\d{2}-\d{2}-\d{2}$/.test(date)) {
      const parts = date.split("-");
      return {
        day: parseInt(parts[0], 10),
        month: parseInt(parts[1], 10),
        year: 2000 + parseInt(parts[2], 10)
      };
    }
    
    // ISO date (YYYY-MM-DD)
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        return {
          day: d.getDate(),
          month: d.getMonth() + 1,
          year: d.getFullYear()
        };
      }
    }
    
    // Date object
    if (date instanceof Date && !isNaN(date.getTime())) {
      return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      };
    }
    
    // Try parsing as Date
    const d = new Date(date);
    if (!isNaN(d.getTime())) {
      return {
        day: d.getDate(),
        month: d.getMonth() + 1,
        year: d.getFullYear()
      };
    }
  } catch {
    // Fall through
  }
  
  return null;
};

/**
 * Compare two dates for monthly sorting (month-year first, then day)
 * This groups all records from the same month together
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @param {string} direction - 'asc' or 'desc'
 * @returns {number} Sort comparison value
 */
export const compareByMonthYear = (date1, date2, direction = 'desc') => {
  const d1 = parseDateComponents(date1);
  const d2 = parseDateComponents(date2);
  
  // Handle null/invalid dates
  if (!d1 && !d2) return 0;
  if (!d1) return direction === 'desc' ? 1 : -1;
  if (!d2) return direction === 'desc' ? -1 : 1;
  
  // Compare year first
  if (d1.year !== d2.year) {
    return direction === 'desc' ? d2.year - d1.year : d1.year - d2.year;
  }
  
  // Then compare month
  if (d1.month !== d2.month) {
    return direction === 'desc' ? d2.month - d1.month : d1.month - d2.month;
  }
  
  // Finally compare day within the same month
  return direction === 'desc' ? d2.day - d1.day : d1.day - d2.day;
};

/**
 * Get month-year key for grouping (YYYYMM format for easy sorting)
 * @param {string|Date} date - Input date
 * @returns {string} YYYYMM format (e.g., "202602" for Feb 2026)
 */
export const getMonthYearKey = (date) => {
  const components = parseDateComponents(date);
  if (!components) return "000000";
  return `${components.year}${String(components.month).padStart(2, "0")}`;
};

/**
 * Get display label for month-year
 * @param {string|Date} date - Input date
 * @returns {string} Display format (e.g., "Feb 2026")
 */
export const getMonthYearDisplay = (date) => {
  const components = parseDateComponents(date);
  if (!components) return "-";
  
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[components.month - 1]} ${components.year}`;
};

/**
 * Sort an array of objects by date field using monthly sorting
 * @param {Array} array - Array to sort
 * @param {string} dateField - Name of the date field
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array} Sorted array
 */
export const sortByMonthYear = (array, dateField, direction = 'desc') => {
  return [...array].sort((a, b) => {
    const dateA = a[dateField];
    const dateB = b[dateField];
    return compareByMonthYear(dateA, dateB, direction);
  });
};

export default {
  formatToDDMMYY,
  parseDDMMYY,
  formatDDMMYYDisplay,
  formatDateDashDisplay,
  getTodayDDMMYY,
  compareDDMMYY,
  isValidDDMMYY,
  toDDMMYY,
  parseDateComponents,
  compareByMonthYear,
  getMonthYearKey,
  getMonthYearDisplay,
  sortByMonthYear
};
